
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { languageMappings } from "@/lib/language-utils";
import { getLocalizedPath } from "./LocalizedRoute";

const LanguageSelector = () => {
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  
  const handleLanguageChange = (newLang: string) => {
    // Get current path
    const currentPath = window.location.pathname;
    
    // Remove existing language prefix if present and get internal path
    const langPrefix = /^\/(en|es|it|de|nl)(\/|$)/;
    let internalPath = currentPath;
    const match = currentPath.match(langPrefix);
    
    if (match) {
      const currentLang = match[1];
      const pathAfterLang = currentPath.substring(match[0].length - (match[2] === '/' ? 1 : 0));
      const localizedSegment = pathAfterLang.replace(/^\//, '');
      
      // Find the internal path from the localized segment
      const langRoutes = {
        en: { "": "/", "gallery": "/galerie", "equipment": "/equipements", "book-now": "/reserver", "contact": "/contact", "surroundings": "/environs", "access": "/acces", "legal": "/mentions-legales", "my-reservations": "/mes-reservations", "profile": "/profil" },
        es: { "": "/", "galeria": "/galerie", "equipamiento": "/equipements", "reservar": "/reserver", "contacto": "/contact", "alrededores": "/environs", "acceso": "/acces", "aviso-legal": "/mentions-legales", "mis-reservas": "/mes-reservations", "perfil": "/profil" },
        it: { "": "/", "galleria": "/galerie", "attrezzature": "/equipements", "prenota": "/reserver", "contatto": "/contact", "dintorni": "/environs", "accesso": "/acces", "note-legali": "/mentions-legales", "le-mie-prenotazioni": "/mes-reservations", "profilo": "/profil" },
        de: { "": "/", "galerie": "/galerie", "ausstattung": "/equipements", "buchen": "/reserver", "kontakt": "/contact", "umgebung": "/environs", "anfahrt": "/acces", "impressum": "/mentions-legales", "meine-buchungen": "/mes-reservations", "profil": "/profil" },
        nl: { "": "/", "galerij": "/galerie", "uitrusting": "/equipements", "boeken": "/reserver", "contact": "/contact", "omgeving": "/environs", "toegang": "/acces", "juridische-kennisgeving": "/mentions-legales", "mijn-reserveringen": "/mes-reservations", "profiel": "/profil" },
      };
      
      const routes = langRoutes[currentLang as keyof typeof langRoutes];
      internalPath = routes?.[localizedSegment] || '/';
    }
    
    // Get the localized path for the new language
    const localizedPath = getLocalizedPath(internalPath, newLang);
    
    // Navigate - the LocalizedRoute will handle setting the language
    navigate(localizedPath);
  };
  
  // Language options with their full names and flags
  const languageOptions = [
    { code: "fr", flag: "🇫🇷", name: t("french") },
    { code: "en", flag: "🇬🇧", name: t("english") },
    { code: "es", flag: "🇪🇸", name: t("spanish") },
    { code: "it", flag: "🇮🇹", name: t("italian") },
    { code: "de", flag: "🇩🇪", name: t("german") },
    { code: "nl", flag: "🇳🇱", name: t("dutch") },
  ];

  // Get current language display info
  const currentLang = languageOptions.find((lang) => lang.code === language);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="flex items-center gap-1">
          <Globe className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">{currentLang?.flag} {currentLang?.name}</span>
          <span className="sm:hidden">{currentLang?.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languageOptions.map((option) => (
          <DropdownMenuItem
            key={option.code}
            onClick={() => handleLanguageChange(option.code)}
            className={language === option.code ? "bg-slate-100" : ""}
          >
            <span className="mr-2">{option.flag}</span>
            {option.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
